import { useState, useEffect } from "react"
import axios from "axios"
import { toast } from "react-toastify"
import Select from "react-select"
import { DataGrid } from "@mui/x-data-grid"

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL

function AddPaymentReceipt() {
  const [customers, setCustomers] = useState([])
  const [selectedCustomer, setSelectedCustomer] = useState(null) // Store selected customer
  const [selectedCustomerName, setSelectedCustomerName] = useState(null) // Stores customer name
  const [invoices, setInvoices] = useState([])
  const [payments, setPayments] = useState([])
  const [creditNotes, setCreditNotes] = useState([])
  const [selectedInvoices, setSelectedInvoices] = useState([]) // For tracking selected invoices
  const [selectedPayments, setSelectedPayments] = useState([]) // For tracking selected payments
  const [selectedCreditNotes, setSelectedCreditNotes] = useState([]) // For tracking selected credit notes
  const [totalPayments, setTotalPayments] = useState(0)
  const [totalInvoiceAmount, setTotalInvoiceAmount] = useState(0)
  const [userRole, setUserRole] = useState("") // State to store user role
  const [organizations, setOrganizations] = useState([]) // List of organizations for Main Admin

  useEffect(() => {
    const fetchUserRoleAndOrganizations = async () => {
      try {
        const token = localStorage.getItem("token")
        if (!token) {
          toast.error("Authentication token is missing. Please log in again.")
          return
        }

        // Fetch user role
        const userResponse = await axios.get(`${API_BASE_URL}/users/specific`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        setUserRole(userResponse.data.role)

        // Fetch organizations if the user is a Main Admin
        if (userResponse.data.role === "Main Admin") {
          const orgResponse = await axios.get(`${API_BASE_URL}/organizations`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          })
          setOrganizations(orgResponse.data.organizations || [])
        }
      } catch (error) {
        console.error("Error fetching user role or organizations:", error)
        toast.error("Error fetching data")
      }
    }

    fetchCustomers()
    fetchUserRoleAndOrganizations()
  }, [])

  useEffect(() => {
    if (selectedCustomer) {
      getInvoices()
    }
  }, [selectedCustomer])

  useEffect(() => {
    if (selectedCustomer) {
      console.log("Selected Customer:", selectedCustomer)
      getPayments()
    }
  }, [selectedCustomer])

  useEffect(() => {
    if (selectedCustomer) {
      getCreditNotes()
    }
  }, [selectedCustomer])

  useEffect(() => {
    // Clear all selected items when customer changes
    if (selectedCustomer) {
      setSelectedInvoices([])
      setSelectedPayments([])
      setSelectedCreditNotes([])
      setTotalPayments(0)
      setTotalInvoiceAmount(0)
    }
  }, [selectedCustomer])

  useEffect(() => {
    const total = selectedInvoices.reduce((acc, curr) => acc + Number.parseFloat(curr.blance), 0)
    setTotalInvoiceAmount(total)
  }, [selectedInvoices])

  useEffect(() => {
    const paymentTotal = selectedPayments.reduce((acc, curr) => {
      const amountToUse = Number.parseFloat(curr.approved_amount) || 0
      return acc + amountToUse
    }, 0)

    const creditNoteTotal = selectedCreditNotes.reduce((acc, curr) => {
      const amountToUse = Number.parseFloat(curr.approved_amount) || 0
      return acc + amountToUse
    }, 0)

    const total = paymentTotal + creditNoteTotal

    if (total > totalInvoiceAmount) {
      toast.error("Total payments and credit notes cannot exceed the total invoice amount.")
      setTotalPayments(totalInvoiceAmount)
    } else {
      setTotalPayments(total)
    }
  }, [selectedPayments, selectedCreditNotes, totalInvoiceAmount])

  const getInvoices = async () => {
    if (!selectedCustomer) {
      setInvoices(null) // No customer selected
      return
    }

    try {
      const token = localStorage.getItem("token")
      if (!token) {
        toast.error("Authentication token is missing. Please log in again.")
        return
      }
      const response = await axios.get(`${API_BASE_URL}/invoices/get/getInvoiceByCustomer/${selectedCustomer}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (response.status === 200 && response.data.length > 0) {
        setInvoices(response.data)
      } else {
        setInvoices(null) // No invoices or empty invoices
      }

      console.log("Invoices:", response.data)
    } catch (error) {
      if (error.response && error.response.status === 404) {
        setInvoices(null) // Handle no invoices found
        toast.info("No invoices found for this customer.")
      } else {
        toast.error("Error fetching invoices")
      }
      console.error("Error fetching invoices:", error.response || error)
    }
  }

  const getPayments = async () => {
    try {
      console.log("Selected Customer Inside the payment:", selectedCustomer)
      const token = localStorage.getItem("token")
      if (!token) {
        toast.error("Authentication token is missing. Please log in again.")
        return
      }
      const response = await axios.get(
        `${API_BASE_URL}/paymentReceipts/get/getPaymentReceiptsByCustomer/${selectedCustomer}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )

      console.log("Payments:", response.data)

      if (response.status === 200 && response.data.length > 0) {
        setPayments(response.data)
      } else {
        setPayments(null) // No payments or empty payments
      }

      console.log("Payments:", response.data)
    } catch (error) {
      if (error.response && error.response.status === 404) {
        setPayments(null) // Handle no payments found
        toast.info("No payments found for this customer.")
      } else {
        toast.error("Error fetching payments")
      }
      console.error("Error fetching payments:", error.response || error)
    }
  }

  const getCreditNotes = async () => {
    try {
      console.log("Selected Customer Inside the payment:", selectedCustomer)
      const token = localStorage.getItem("token")
      if (!token) {
        toast.error("Authentication token is missing. Please log in again.")
        return
      }
      const response = await axios.get(`${API_BASE_URL}/creditNotes/getCreditNoteByCustomer/${selectedCustomer}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      console.log("Credit Notes:", response.data)

      if (response.status === 200 && response.data.length > 0) {
        setCreditNotes(response.data)
      } else {
        setCreditNotes(null) // No payments or empty payments
      }

      console.log("Credit Notes:", response.data)
    } catch (error) {
      if (error.response && error.response.status === 404) {
        setCreditNotes(null) // Handle no payments found
        toast.info("No credit notes found for this customer.")
      } else {
        toast.error("Error fetching credit notes")
      }
      console.error("Error fetching credit notes:", error.response || error)
    }
  }

  const fetchCustomers = async () => {
    try {
      const token = localStorage.getItem("token")
      if (!token) {
        toast.error("Authentication token is missing. Please log in again.")
        return
      }
      const response = await axios.get(`${API_BASE_URL}/customers/`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      setCustomers(response.data.customers)
    } catch (error) {
      console.error("Error fetching customers:", error)
      toast.error("Error fetching customers")
    }
  }

  const customCustomerIDSelect = (customerOptions, selectedCustomer, setSelectedCustomer, setSelectedCustomerName) => {
    return (
      <Select
        options={customerOptions}
        getOptionLabel={(e) => e.customer_code} // Show customer ID in dropdown
        getOptionValue={(e) => e._id} // Use _id as value
        isSearchable={true} // Enable search functionality
        placeholder="Search by Customer ID"
        className="w-full"
        value={customerOptions.find((option) => option._id === selectedCustomer) || null}
        onChange={(selectedOption) => {
          setSelectedCustomer(selectedOption?._id) // Set selected customer ID
          setSelectedCustomerName(selectedOption?.customer_name) // Auto-fill the customer name
        }}
      />
    )
  }

  const customCustomerNameSelect = (
    customerOptions,
    selectedCustomerName,
    setSelectedCustomerName,
    setSelectedCustomer,
  ) => {
    return (
      <Select
        options={customerOptions}
        getOptionLabel={(e) => e.customer_name} // Show customer name in dropdown
        getOptionValue={(e) => e._id} // Use _id as value
        isSearchable={true} // Enable search functionality
        placeholder="Search by Customer Name"
        className="w-full"
        value={customerOptions.find((option) => option.customer_name === selectedCustomerName) || null}
        onChange={(selectedOption) => {
          setSelectedCustomer(selectedOption?._id) // Auto-fill customer ID
          setSelectedCustomerName(selectedOption?.customer_name) // Set selected customer name
        }}
      />
    )
  }

  const handleAddInvoice = (invoice) => {
    // Check if there is already an invoice selected
    if (selectedInvoices.length > 0) {
      // Display an error toast message
      toast.error("You can only add one invoice at a time for settlement.")
      return // Exit the function without adding the new invoice
    }

    // If no invoice is selected, set the selected invoice
    setSelectedInvoices([invoice])

    // Optionally, remove the selected invoice from the list of all invoices
    // If you do not wish to remove the invoice from the table, you can omit this step
    setInvoices((currentInvoices) => currentInvoices.filter((inv) => inv.invoice_id !== invoice.invoice_id))
  }

  const handleAddCreditNote = (creditNote) => {
    setSelectedCreditNotes((currentCreditNotes) => [...currentCreditNotes, creditNote])

    setCreditNotes((currentCreditNotes) => currentCreditNotes.filter((credit) => credit._id !== creditNote._id))
  }

  const handleAddPayment = (payment) => {
    setSelectedPayments((currentPayments) => [...currentPayments, payment])

    setPayments((currentPayments) =>
      currentPayments.filter((pay) => pay.payment_receipt_id !== payment.payment_receipt_id),
    )
  }

  const handleRemoveSelectedCreditNote = (creditNote) => {
    // Add the removed credit note back to the list of credit notes
    setCreditNotes((currentCreditNotes) => [...currentCreditNotes, creditNote])

    // Remove only the specific credit note from selected credit notes
    setSelectedCreditNotes((currentCreditNotes) => currentCreditNotes.filter((cn) => cn._id !== creditNote._id))
  }

  const handleRemoveSelectedInvoice = (invoice) => {
    // Add the removed invoice back to the list of invoices
    setInvoices((currentInvoices) => [...currentInvoices, invoice])

    // Clear the selected invoices
    setSelectedInvoices([])
  }

  const handleRemovePayment = (payment_receipt_id) => {
    setSelectedPayments((currentPayments) =>
      currentPayments.filter((pay) => pay.payment_receipt_id !== payment_receipt_id),
    )

    setPayments((currentPayments) => [
      ...currentPayments,
      selectedPayments.find((pay) => pay.payment_receipt_id === payment_receipt_id),
    ])
  }

  const handlePaymentAmountChange = (event, payment) => {
    const newAmount = Number.parseFloat(event.target.value) || 0

    // Check if the new amount exceeds the amount available in the payment receipt
    if (newAmount > payment.blance) {
      toast.error(`Amount cannot exceed available receipt balance of ${payment.blance}`)
      event.target.value = payment.approved_amount || 0 // Reset to last valid or 0 if none set
      return // Prevent further execution
    }

    // Calculate potential new total including both payments and credit notes
    const paymentTotal = selectedPayments.reduce((acc, curr) => {
      if (curr.payment_receipt_id === payment.payment_receipt_id) {
        return acc + newAmount
      } else {
        return acc + (Number.parseFloat(curr.approved_amount) || 0)
      }
    }, 0)

    const creditNoteTotal = selectedCreditNotes.reduce((acc, curr) => {
      return acc + (Number.parseFloat(curr.approved_amount) || 0)
    }, 0)

    const potentialNewTotal = paymentTotal + creditNoteTotal

    if (potentialNewTotal > totalInvoiceAmount) {
      toast.error("Total payments and credit notes cannot exceed the total invoice amount.")
      event.target.value = payment.approved_amount // Reset the value to the last valid state
      return // Exit the function to prevent state update
    }

    const updatedPayments = selectedPayments.map((p) => {
      if (p.payment_receipt_id === payment.payment_receipt_id) {
        return { ...p, approved_amount: newAmount }
      }
      return p
    })
    setSelectedPayments(updatedPayments)
  }

  const handleCreditNoteAmountChange = (event, creditNote) => {
    const newAmount = Number.parseFloat(event.target.value) || 0

    // Check if the new amount exceeds the available balance in the credit note
    if (newAmount > creditNote.balance) {
      toast.error(`Amount cannot exceed available credit note balance of ${creditNote.balance}`)
      event.target.value = creditNote.approved_amount || creditNote.balance
      return
    }

    // Calculate potential new total including both payments and credit notes
    const paymentTotal = selectedPayments.reduce((acc, curr) => {
      return acc + (Number.parseFloat(curr.approved_amount) || 0)
    }, 0)

    const creditNoteTotal = selectedCreditNotes.reduce((acc, curr) => {
      if (curr._id === creditNote._id) {
        return acc + newAmount
      } else {
        return acc + (Number.parseFloat(curr.approved_amount) || 0)
      }
    }, 0)

    const potentialNewTotal = paymentTotal + creditNoteTotal

    if (potentialNewTotal > totalInvoiceAmount) {
      toast.error("Total payments and credit notes cannot exceed the total invoice amount.")
      event.target.value = creditNote.approved_amount || ""
      return
    }

    const updatedCreditNotes = selectedCreditNotes.map((cn) => {
      if (cn._id === creditNote._id) {
        return { ...cn, approved_amount: newAmount }
      }
      return cn
    })
    setSelectedCreditNotes(updatedCreditNotes)
  }

  const handleSubmit = async () => {
    try {
      const token = localStorage.getItem("token")

      if (!token) {
        toast.error("Authentication token is missing. Please log in again.")
        return
      }

      console.log("Selected Invoice:", selectedInvoices[0]._id)

      const response = await axios.post(
        `${API_BASE_URL}/settlements/add`,
        {
          settlement_id: "SETTLEMENT_ID", // Replace with actual settlement ID
          invoiceId: selectedInvoices[0]._id,
          credit_notes: selectedCreditNotes,
          payment_receipts: selectedPayments,
          settledAmount: totalPayments,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )

      console.log("Settlement created:", response.data)
      console.log("Invoice settled successfully")
      toast.success("Invoice settled successfully")
    } catch (error) {
      console.error("Error settling invoice:", error)
      toast.error("Error settling invoice")
    }
  }

  return (
    <div className="container mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h2 className="text-2xl font-bold text-green-700 mb-6">Receipt Settelement</h2>
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-gray-700">Select Customer ID:</label>
            {customCustomerIDSelect(customers, selectedCustomer, setSelectedCustomer, setSelectedCustomerName)}
          </div>
          <div>
            <label className="block text-gray-700">Select Customer Name:</label>
            {customCustomerNameSelect(customers, selectedCustomerName, setSelectedCustomerName, setSelectedCustomer)}
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div>
            <h3 className="text-xl font-semibold">Invoices</h3>
            <DataGrid
              rows={invoices}
              getRowId={(row) => row.invoice_id}
              columns={[
                { field: "invoice_id", headerName: "Invoice ID", flex: 1.5 },
                {
                  field: "total_amount",
                  headerName: "Amount",
                  flex: 0.8,
                  renderCell: (params) => {
                    return <span>{params.row.sales_order_id?.total_amount}</span>
                  },
                },
                { field: "blance", headerName: "Balance", flex: 0.8 },
                {
                  field: "status",
                  headerName: "Status",
                  flex: 0.8,
                  renderCell: (params) => {
                    const status = params.row.status

                    // Map statuses to classes
                    const statusClassMap = {
                      Opend: "bg-lime-600 text-white",
                      "Partially Settled": "bg-blue-500 text-white",
                      "Fully Settled": "bg-green-500 text-white",
                    }

                    // Get the class based on status or default to a neutral color
                    const statusClass = statusClassMap[status] || "bg-gray-500 text-white" // Default class if status not found

                    return <span className={`px-2 py-1 rounded-sm ${statusClass}`}>{status}</span>
                  },
                },
                {
                  field: "action",
                  headerName: "Action",
                  flex: 0.8,
                  renderCell: (params) => {
                    return (
                      <button
                        className="bg-green-500 text-white font-bold py-1.5 px-4 rounded text-xs"
                        onClick={() => handleAddInvoice(params.row)}
                        //disabled={selectedInvoices.length > 0}
                      >
                        Add
                      </button>
                    )
                  },
                },
              ]}
            />
          </div>
          <div>
            <h3 className="text-xl font-semibold">Credit Notes</h3>
            <DataGrid
              rows={creditNotes}
              getRowId={(row) => row._id}
              columns={[
                { field: "credit_note_code", headerName: "Credit Note ID", flex: 1.5 },
                { field: "total_credit_amount", headerName: "Amount", flex: 0.8 },
                { field: "balance", headerName: "Balance", flex: 0.8 },
                {
                  field: "status",
                  headerName: "Status",
                  flex: 1,
                  renderCell: (params) => {
                    const status = params.row.status

                    // Map statuses to classes
                    const statusClassMap = {
                      Pending: "bg-lime-600 text-white",
                      Processed: "bg-blue-500 text-white",
                      Closed: "bg-green-500 text-white",
                    }

                    // Get the class based on status or default to a neutral color
                    const statusClass = statusClassMap[status] || "bg-gray-500 text-white" // Default class if status not found

                    return <span className={`px-2 py-1 rounded-sm ${statusClass}`}>{status}</span>
                  },
                },
                {
                  field: "action",
                  headerName: "Action",
                  flex: 0.8,
                  renderCell: (params) => {
                    return (
                      <button
                        className="bg-green-500 text-white font-bold py-1.5 px-4 rounded text-xs"
                        onClick={() => handleAddCreditNote(params.row)}
                      >
                        Add
                      </button>
                    )
                  },
                },
              ]}
            />
          </div>
          <div>
            <h3 className="text-xl font-semibold">Payments</h3>
            <DataGrid
              rows={payments}
              getRowId={(row) => row.payment_receipt_id}
              columns={[
                { field: "payment_receipt_id", headerName: "Payment Receipt ID", flex: 1.5 },
                { field: "amount", headerName: "Amount", flex: 0.8 },
                { field: "blance", headerName: "Balance", flex: 0.8 },
                {
                  field: "status",
                  headerName: "Status",
                  flex: 1,
                  renderCell: (params) => {
                    const status = params.row.status

                    // Map statuses to classes
                    const statusClassMap = {
                      Pending: "bg-lime-600 text-white",
                      Processed: "bg-blue-500 text-white",
                      Closed: "bg-green-500 text-white",
                    }

                    // Get the class based on status or default to a neutral color
                    const statusClass = statusClassMap[status] || "bg-gray-500 text-white" // Default class if status not found

                    return <span className={`px-2 py-1 rounded-sm ${statusClass}`}>{status}</span>
                  },
                },
                {
                  field: "action",
                  headerName: "Action",
                  flex: 0.8,
                  renderCell: (params) => {
                    return (
                      <button
                        className="bg-green-500 text-white font-bold py-1.5 px-4 rounded text-xs"
                        onClick={() => handleAddPayment(params.row)}
                      >
                        Add
                      </button>
                    )
                  },
                },
              ]}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 p-6">
          <div className="border-2 p-4 rounded-lg">
            <h3 className="text-lg font-semibold mb-4">Selected Invoices</h3>
            <div className="grid grid-cols-4 text-center mb-2 font-semibold">
              <div>Invoice ID</div>
              <div>Amount</div>
              <div>Balance</div>
              <div>Action</div>
            </div>
            <ul className="border p-4 rounded-lg mt-4">
              {selectedInvoices.map((invoice) => (
                <li key={invoice.invoice_id} className="flex justify-between items-center mb-2 border-b pb-2">
                  <div>{invoice.invoice_id}</div>
                  <div>{invoice.sales_order_id.final_amount}</div>
                  <div>{invoice.blance}</div>
                  <div>{}</div>
                  <button
                    className="px-3 py-1 rounded bg-red-500 text-white text-xs"
                    onClick={() => handleRemoveSelectedInvoice(selectedInvoices[0])}
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>
            <div className="text-left mt-6">
              <h3 className="text-xl font-bold text-black mb-6">
                Total Invoice Amount (LKR): {totalInvoiceAmount.toFixed(2)}
              </h3>
            </div>
          </div>
          <div className="border-2 p-4 rounded-lg">
            <h3 className="text-lg font-semibold mb-4">Selected Payment Receipts / Credit Notes</h3>
            <div className="grid grid-cols-5 text-center mb-2 font-semibold">
              <div>ID</div>
              <div>Amount</div>
              <div>Balance</div>
              <div>Approved Amount</div>
              <div>Action</div>
            </div>

            <ul className="border p-4 rounded-lg mt-4">
              {/* Render selected payment receipts */}
              {selectedPayments.map((payment) => (
                <li
                  key={`payment-${payment.payment_receipt_id}`}
                  className="grid grid-cols-5 items-center text-center mb-2 border-b pb-2"
                >
                  <div>{payment.payment_receipt_id}</div>
                  <div>{payment.amount}</div>
                  <div>{payment.blance}</div>
                  <div>
                    <input
                      type="number"
                      value={payment.approved_amount || ""}
                      onChange={(e) => handlePaymentAmountChange(e, payment)}
                      className="w-32 p-1 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      placeholder="Enter amount"
                    />
                  </div>
                  <div>
                    <button
                      className="px-2 py-1 rounded bg-red-500 text-white text-xs"
                      onClick={() => handleRemovePayment(payment.payment_receipt_id)}
                    >
                      Remove
                    </button>
                  </div>
                </li>
              ))}

              {/* Render selected credit notes */}
              {selectedCreditNotes.map((creditNote) => (
                <li key={`credit-${creditNote._id}`} className="grid grid-cols-5 items-center text-center mb-2 border-b pb-2">
                  <div>{creditNote.credit_note_code}</div>
                  <div>{creditNote.total_credit_amount}</div>
                  <div>{creditNote.balance}</div>
                  <div>
                    <input
                      type="number"
                      value={creditNote.approved_amount || ""}
                      onChange={(e) => handleCreditNoteAmountChange(e, creditNote)}
                      className="w-32 p-1 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      placeholder="Enter amount"
                    />
                  </div>
                  <div>
                    <button
                      className="px-2 py-1 rounded bg-red-500 text-white text-xs"
                      onClick={() => handleRemoveSelectedCreditNote(creditNote)}
                    >
                      Remove
                    </button>
                  </div>
                </li>
              ))}
            </ul>

            <div className="text-left mt-6">
              <h3 className="text-xl font-bold text-black mb-6">
                Total Payment Amount (LKR): {totalPayments.toFixed(2)}
              </h3>
            </div>
          </div>
        </div>
        <div className="text-left mt-6">
          <h3 className="text-xl font-bold text-black mb-6">
            Remaining Amount to Settle (LKR): {(totalInvoiceAmount - totalPayments).toFixed(2)}
          </h3>
          {totalInvoiceAmount > 0 && totalInvoiceAmount - totalPayments === 0 && (
            <h3 className="text-xl font-bold text-green-500">Invoice is fully settled!</h3>
          )}
        </div>
        <div className="text-right mt-6">
          <button onClick={handleSubmit} className="bg-green-700 text-white py-3 px-6 rounded-lg hover:bg-blue-600">
            Settle Invoice
          </button>
        </div>
      </div>
    </div>
  )
}

export default AddPaymentReceipt
